/**
 * MeasureTypeSelector - 계측 타입 선택 풀스크린 모달
 * Feature: #393
 *
 * 24개 계측 타입을 7개 카테고리별로 그룹화하여 표시
 * 모바일에 최적화된 풀스크린 bottom sheet 스타일
 *
 * @see packages/web/components/document-editor/tabs/measure/MeasureTypeSelector.tsx
 */
import {
  MeasureType,
  MeasureCategory,
  MeasureTypesByCategory,
  MeasureTypeNames,
  MeasureCategoryNames,
  MeasureUnits,
  type MeasureCategoryType,
  type MeasureTypeKeyType,
} from "@ict/shared";
import { X, Zap, Radio, Waves, Wifi, Activity, CreditCard, MoreHorizontal } from "lucide-react";
import { measureTypeIcons } from "./measureTypeIcons";

interface MeasureTypeSelectorProps {
  open: boolean;
  onClose: () => void;
  onSelect: (measureType: MeasureType) => void;
  /** 이미 등록된 계측 타입 목록 - 비활성화 표시 */
  registeredTypes?: MeasureType[];
}

// 카테고리 순서
const categoryOrder: MeasureCategoryType[] = [
  MeasureCategory.ELECTRICAL,
  MeasureCategory.COMMUNICATION,
  MeasureCategory.RADIO_WAVE,
  MeasureCategory.WIRELESS,
  MeasureCategory.FIELD_STRENGTH,
  MeasureCategory.RF_CARD,
  MeasureCategory.MISC,
];

// 카테고리별 아이콘
const categoryIcons: Record<MeasureCategoryType, React.ComponentType<{ className?: string }>> = {
  [MeasureCategory.ELECTRICAL]: Zap,
  [MeasureCategory.COMMUNICATION]: Radio,
  [MeasureCategory.RADIO_WAVE]: Waves,
  [MeasureCategory.WIRELESS]: Wifi,
  [MeasureCategory.FIELD_STRENGTH]: Activity,
  [MeasureCategory.RF_CARD]: CreditCard,
  [MeasureCategory.MISC]: MoreHorizontal,
};

// 카테고리별 색상 (bg, text, border)
const categoryColors: Record<
  MeasureCategoryType,
  { bg: string; text: string; border: string; badge: string }
> = {
  [MeasureCategory.ELECTRICAL]: {
    bg: "bg-amber-50",
    text: "text-amber-600",
    border: "border-amber-200",
    badge: "bg-amber-100 text-amber-700",
  },
  [MeasureCategory.COMMUNICATION]: {
    bg: "bg-blue-50",
    text: "text-blue-600",
    border: "border-blue-200",
    badge: "bg-blue-100 text-blue-700",
  },
  [MeasureCategory.RADIO_WAVE]: {
    bg: "bg-violet-50",
    text: "text-violet-600",
    border: "border-violet-200",
    badge: "bg-violet-100 text-violet-700",
  },
  [MeasureCategory.WIRELESS]: {
    bg: "bg-cyan-50",
    text: "text-cyan-600",
    border: "border-cyan-200",
    badge: "bg-cyan-100 text-cyan-700",
  },
  [MeasureCategory.FIELD_STRENGTH]: {
    bg: "bg-emerald-50",
    text: "text-emerald-600",
    border: "border-emerald-200",
    badge: "bg-emerald-100 text-emerald-700",
  },
  [MeasureCategory.RF_CARD]: {
    bg: "bg-rose-50",
    text: "text-rose-600",
    border: "border-rose-200",
    badge: "bg-rose-100 text-rose-700",
  },
  [MeasureCategory.MISC]: {
    bg: "bg-slate-50",
    text: "text-slate-600",
    border: "border-slate-200",
    badge: "bg-slate-100 text-slate-700",
  },
};

export function MeasureTypeSelector({
  open,
  onClose,
  onSelect,
  registeredTypes,
}: MeasureTypeSelectorProps) {
  if (!open) return null;

  const handleSelect = (measureType: MeasureType) => {
    onSelect(measureType);
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      {/* 헤더 */}
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <h2 className="text-[17px] font-semibold text-gray-900">측정 항목 선택</h2>
        <button
          type="button"
          onClick={onClose}
          className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-gray-100 active:bg-gray-200"
        >
          <X className="h-5 w-5 text-gray-500" />
        </button>
      </div>

      {/* 카테고리별 타입 목록 */}
      <div className="flex-1 overflow-y-auto overscroll-contain px-4 pb-safe-area-inset-bottom">
        <p className="py-3 text-[13px] text-gray-400">추가할 계측 항목을 선택하세요 (총 24개)</p>

        <div className="space-y-5 pb-6">
          {categoryOrder.map((category) => {
            const types = MeasureTypesByCategory[category];
            const Icon = categoryIcons[category];
            const colors = categoryColors[category];

            return (
              <div key={category}>
                {/* 카테고리 헤더 */}
                <div
                  className={`mb-2 flex items-center gap-2.5 rounded-lg border px-3 py-2.5 ${colors.bg} ${colors.border}`}
                >
                  <div
                    className={`flex h-7 w-7 items-center justify-center rounded-md bg-white shadow-sm`}
                  >
                    <Icon className={`h-4 w-4 ${colors.text}`} />
                  </div>
                  <span className="flex-1 text-[14px] font-semibold text-gray-800">
                    {MeasureCategoryNames[category]}
                  </span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${colors.badge}`}
                  >
                    {types.length}
                  </span>
                </div>

                {/* 타입 목록 */}
                <div className="grid grid-cols-2 gap-2">
                  {types.map((typeKey) => {
                    const measureType = typeKey as unknown as MeasureType;
                    const typeName = MeasureTypeNames[typeKey as MeasureTypeKeyType];
                    const unit = MeasureUnits[typeKey as MeasureTypeKeyType];
                    const isRegistered = registeredTypes?.includes(measureType) ?? false;

                    const TypeIcon = measureTypeIcons[typeKey as MeasureTypeKeyType];

                    return (
                      <button
                        key={typeKey}
                        type="button"
                        onClick={() => handleSelect(measureType)}
                        disabled={isRegistered}
                        className={`flex items-start rounded-lg border px-3 py-3 text-left ${
                          isRegistered
                            ? "border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed"
                            : "border-gray-200 bg-white active:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {TypeIcon && (
                            <TypeIcon
                              className={`h-4 w-4 flex-shrink-0 ${isRegistered ? "text-gray-300" : colors.text}`}
                            />
                          )}
                          <div className="flex flex-col gap-0.5">
                            <span
                              className={`text-[13px] font-medium leading-tight ${isRegistered ? "text-gray-400" : "text-gray-800"}`}
                            >
                              {typeName}
                            </span>
                            <span
                              className={`text-[11px] ${isRegistered ? "text-gray-300" : "text-gray-400"}`}
                            >
                              {isRegistered ? "등록됨" : unit}
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
